"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Flame, Droplets, Wrench, Zap, ShieldAlert, PhoneCall, Clock } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const problemCategories = [{
  id: "burst-pipes",
  title: "Burst & Leaking Pipes",
  urgency: "Immediate Hazard",
  urgencyVariant: "destructive" as const,
  description: "Active wall leaks, ceiling drips, slab leaks, and ruptured supply lines that risk structural flooding.",
  commonIssues: "Ceiling stains, sudden pressure drop, running water sounds",
  linkTo: "/problems/",
  iconName: "Droplets"
}, {
  id: "water-heater",
  title: "Water Heater Breakdown",
  urgency: "High Priority",
  urgencyVariant: "default" as const,
  description: "Total loss of hot water, rust-colored water, leaking tank bottom, or hazardous pilot and burner issues.",
  commonIssues: "Lukewarm water, rumbling sounds, standing tank leaks",
  linkTo: "/water-heater/",
  iconName: "Flame"
}, {
  id: "clogged-drains",
  title: "Severe Drain & Toilet Clogs",
  urgency: "High Priority",
  urgencyVariant: "default" as const,
  description: "Stubborn kitchen or bathroom blockages, standing sewage, gurgling pipes, and multi-fixture backups.",
  commonIssues: "Slow drains, toilet overflow, foul sewer odors",
  linkTo: "/problems/",
  iconName: "Wrench"
}, {
  id: "sewer-backup",
  title: "Main Sewer Line Backups",
  urgency: "Biohazard Alert",
  urgencyVariant: "destructive" as const,
  description: "Raw sewage backing up into tubs or floor drains caused by tree root intrusion or collapsed clay pipes.",
  commonIssues: "Multiple blocked fixtures, yard wet spots, noxious fumes",
  linkTo: "/problems/",
  iconName: "ShieldAlert"
}, {
  id: "water-pressure",
  title: "Low Water Pressure",
  urgency: "System Diagnostic",
  urgencyVariant: "secondary" as const,
  description: "Unexplained drops in household water volume, faulty pressure regulator valves, or hidden main leaks.",
  commonIssues: "Weak shower flow, simultaneous tap trickle, pipe whistling",
  linkTo: "/services/",
  iconName: "Zap"
}, {
  id: "emergency-repairs",
  title: "24/7 Urgent Diagnostics",
  urgency: "Dispatch Ready",
  urgencyVariant: "destructive" as const,
  description: "Unidentified leaks, strange water discoloration, or middle-of-the-night plumbing catastrophes.",
  commonIssues: "Unlocatable leaks, meter spinning rapidly, emergency shutoffs",
  linkTo: "/contact/",
  iconName: "AlertTriangle"
}];
export default function ProblemCategories() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "Droplets":
        return <Droplets className="size-6 text-primary" />;
      case "Flame":
        return <Flame className="size-6 text-accent" />;
      case "Wrench":
        return <Wrench className="size-6 text-primary" />;
      case "ShieldAlert":
        return <ShieldAlert className="size-6 text-destructive" />;
      case "Zap":
        return <Zap className="size-6 text-primary" />;
      default:
        return <AlertTriangle className="size-6 text-destructive" />;
    }
  };
  return <section id="relatedproblemcategories" data-nav="light" className="relative bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-3">
              <Clock className="size-3.5" />
              <span>Troubleshoot by Problem Type</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground tracking-tight">
              Related Emergency & Repair Categories
            </h2>
            <p className="mt-3 text-base md:text-lg text-muted-foreground">
              Identify your exact issue below for step-by-step guidance, diagnostic insights, and immediate service dispatch across Jonesboro and surrounding areas.
            </p>
          </div>

          <div className="shrink-0 flex items-center gap-3">
            <Button asChild variant="outline" className="h-11 px-5 border-border">
              <Link to="/services/" className="inline-flex items-center gap-2">
                <span>View Full Service Catalog</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {problemCategories.map((category, index) => {
          return <motion.div key={category.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.4,
            delay: index * 0.08
          }} className="h-full flex">
                <Card className="flex flex-col justify-between w-full h-full border border-border/80 hover:border-primary/40 transition-[border-color,box-shadow] hover:shadow-md bg-card">
                  <div>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="p-2.5 rounded-lg bg-background border border-border shadow-xs">
                          {getIcon(category.iconName)}
                        </div>
                        <Badge variant={category.urgencyVariant} className="text-[11px] font-medium">
                          {category.urgency}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-semibold text-card-foreground">
                        {category.title}
                      </CardTitle>
                      <CardDescription className="text-sm mt-1.5 leading-relaxed">
                        {category.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-2">
                      <div className="p-3 rounded-lg bg-background/80 border border-border/60">
                        <p className="text-xs font-semibold text-foreground uppercase tracking-wide mb-1">
                          Common Warning Signs:
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {category.commonIssues}
                        </p>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-4 mt-auto border-t border-border/50 flex items-center justify-between gap-3">
                    <Button asChild variant="ghost" size="sm" className="px-0 hover:bg-transparent text-primary hover:text-primary/80 font-medium group">
                      <Link to={category.linkTo} className="inline-flex items-center gap-1.5">
                        <span>Explore Solutions</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>

                    <Button asChild size="sm" variant="outline" className="h-8 px-3 text-xs bg-secondary hover:bg-secondary/80 border-transparent text-secondary-foreground">
                      <Link to="/contact/" className="inline-flex items-center gap-1.5">
                        <PhoneCall className="size-3.5 text-accent-foreground" />
                        <span>Request Plumber</span>
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Quick Assistance Banner */}
        <div className="mt-12 rounded-xl bg-primary text-primary-foreground p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-md">
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex size-12 rounded-full bg-primary-foreground/10 items-center justify-center shrink-0">
              <PhoneCall className="size-6 text-accent" />
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-bold font-default leading-tight">
                Not sure what kind of plumbing problem you have?
              </h3>
              <p className="text-sm text-primary-foreground/80 mt-1">
                Our emergency dispatchers in Jonesboro can diagnose the situation over the phone and dispatch a licensed technician immediately.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild variant="secondary" size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/contact/">
                Get Immediate Help
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-transparent text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/10 hover:text-primary-foreground">
              <Link to="/problems/">
                View All Problems
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}